import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, Info, CheckCircle2, Clock, AlertCircle, ArrowLeft, Lock, Unlock, Upload, Download, File, Trash2, Shield } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { generateApplicationReceipt } from "../lib/pdf";

const STATUS_ORDER = [
  "received", "under_review", "info_required", "verification", "assessment",
  "community_assessment", "approved", "waiting_funding", "planning",
  "in_progress", "completed", "not_approved",
];

const STATUS_LABEL = {
  received: "Received",
  under_review: "Under initial review",
  info_required: "Information required",
  verification: "Verification in progress",
  assessment: "Assessment",
  community_assessment: "Community assessment required",
  approved: "Approved",
  waiting_funding: "Waiting for funding",
  planning: "Implementation planning",
  in_progress: "In progress",
  completed: "Completed",
  not_approved: "Not approved",
};

const STATUS_DESC = {
  received: "Your application has been received.",
  under_review: "Our team is reviewing your information.",
  info_required: "We need additional information before proceeding.",
  verification: "The information provided is currently being verified.",
  assessment: "Your request is being assessed against programme criteria.",
  community_assessment: "A community consultation or site visit may be required.",
  approved: "Your application has been approved subject to programme conditions.",
  waiting_funding: "The need has been approved, but funding is not yet available.",
  planning: "The Foundation is preparing the intervention.",
  in_progress: "Support or project implementation has commenced.",
  completed: "The intervention has been completed.",
  not_approved: "The Foundation is unable to support this request at this time.",
};

const NICE = (k) => ({
  individual: "Individual support", family: "Family support", empowerment: "Empowerment / livelihood",
  community: "Community intervention", organisation: "Organisation", education: "Education support",
  food: "Food support", emergency: "Emergency", agriculture: "Agriculture", health: "Health",
  water: "Water", energy: "Energy", women_youth: "Women & youth", partnership: "Partnership",
}[k] || k);

export default function ApplyTrack() {
  const [sp] = useSearchParams();
  const [serial, setSerial] = useState(sp.get("serial") || "");
  const [contact, setContact] = useState("");
  const [status, setStatus] = useState(null);   // quick public track
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Portal (docs)
  const [token, setToken] = useState(() => sessionStorage.getItem("dk_portal_token") || "");
  const [portalApp, setPortalApp] = useState(null);
  const [docs, setDocs] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => { document.title = "Track my application | Dutsen Kauna Foundation"; }, []);

  const doLookup = async (e) => {
    e && e.preventDefault();
    if (!serial) return;
    setLoading(true); setError(""); setStatus(null);
    try {
      const params = new URLSearchParams({ serial });
      if (contact) params.set("contact", contact);
      const { data } = await api.get(`/applications/track?${params.toString()}`);
      setStatus(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const doUnlock = async () => {
    if (!serial || !contact) { toast.error("Serial and contact are both required to unlock the portal."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/portal/unlock", { serial, contact });
      sessionStorage.setItem("dk_portal_token", data.token);
      setToken(data.token);
      toast.success("Portal unlocked for 24h.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const logoutPortal = () => {
    sessionStorage.removeItem("dk_portal_token");
    setToken(""); setPortalApp(null); setDocs([]);
  };

  // Load portal app+docs when we have a token
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.get(`/portal/application?token=${encodeURIComponent(token)}`);
        setPortalApp(data);
        setSerial(data.serial);
        const dRes = await api.get(`/portal/documents?token=${encodeURIComponent(token)}`);
        setDocs(dRes.data);
      } catch (e) {
        logoutPortal();
      }
    })();
  }, [token]);

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10 MB per file."); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = String(reader.result || "").split(",")[1] || "";
      try {
        await api.post(`/portal/documents?token=${encodeURIComponent(token)}`, {
          filename: file.name, content_type: file.type || "application/octet-stream", data_base64: b64,
        });
        toast.success("Document uploaded");
        const dRes = await api.get(`/portal/documents?token=${encodeURIComponent(token)}`);
        setDocs(dRes.data);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    };
    reader.readAsDataURL(file);
  };

  const downloadDoc = async (doc) => {
    try {
      const { data } = await api.get(`/portal/documents/${doc.id}/download?token=${encodeURIComponent(token)}`);
      const link = document.createElement("a");
      link.href = `data:${data.content_type};base64,${data.data_base64}`;
      link.download = data.filename; link.click();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Delete document?")) return;
    await api.delete(`/portal/documents/${id}?token=${encodeURIComponent(token)}`);
    setDocs(docs.filter((d) => d.id !== id));
    toast.success("Deleted");
  };

  const activeIdx = status ? STATUS_ORDER.indexOf(status.status) : -1;

  return (
    <div data-testid="apply-track-page">
      {/* Hero */}
      <section className="relative bg-[#155e3f] text-white py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] to-[#155e3f]" />
        <div className="dk-container relative">
          <Link to="/apply" className="text-[#d6aa43] text-sm font-semibold inline-flex items-center gap-1 mb-4"><ArrowLeft size={14} /> Apply for support</Link>
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Application status</span>
          <h1 className="font-serif text-4xl md:text-5xl font-bold mt-2 max-w-3xl">Track my application.</h1>
          <p className="mt-3 text-white max-w-xl">Enter your serial number to see the current status. Add your registered email or phone to unlock the full portal, view internal status notes and upload additional documents.</p>
        </div>
      </section>

      <section className="py-14 bg-[#f8f4e8]">
        <div className="dk-container max-w-4xl">
          {/* Lookup form */}
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-black/5">
            <form onSubmit={doLookup} className="grid md:grid-cols-[1fr,1fr,auto] gap-3 items-end" data-testid="track-form">
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">Serial number</label>
                <input value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="e.g. DKF-EMP-2026-000001"
                  className="w-full px-4 py-3 border border-black/15 rounded-md font-mono" data-testid="track-serial-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">Email or phone (optional for status, required for portal)</label>
                <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Enter your registered contact"
                  className="w-full px-4 py-3 border border-black/15 rounded-md" data-testid="track-contact-input" />
              </div>
              <button type="submit" disabled={loading} className="dk-btn-primary !py-3 whitespace-nowrap" data-testid="track-lookup-btn">
                {loading ? "Checking…" : <><Search size={14} /> Check status</>}
              </button>
            </form>
            {contact && !token && (
              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <button onClick={doUnlock} className="dk-btn-outline !text-[#0d1724] !border-[#0d1724] !py-2 hover:!bg-[#155e3f] hover:!text-[#d6aa43]" data-testid="portal-unlock-btn">
                  <Unlock size={14} /> Unlock full portal
                </button>
                <span className="text-xs text-[#0a0d12]/60"><Shield size={12} className="inline text-[#d6aa43]" /> Portal session lasts 24h. No password required.</span>
              </div>
            )}
            {error && <div className="mt-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2"><AlertCircle size={16} className="flex-shrink-0 mt-0.5" /> {error}</div>}
          </div>

          {/* Quick status */}
          {status && !portalApp && (
            <div className="mt-8 bg-white rounded-2xl p-8 md:p-10 border border-black/5" data-testid="track-status-panel">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#0a0d12]/60">Reference</div>
                  <div className="font-mono text-lg font-bold text-[#0a0d12]">{status.serial}</div>
                  <div className="text-sm text-[#0a0d12]/70 mt-1">{NICE(status.primary_category)}</div>
                  <div className="text-sm text-[#0a0d12]/80 mt-2">Applicant: <strong>{status.applicant_name}</strong></div>
                </div>
                <div className="px-4 py-2 rounded-md bg-[#155e3f] text-[#d6aa43]">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white">Current status</div>
                  <div className="font-serif text-xl font-bold" data-testid="track-status-label">{status.status_label}</div>
                </div>
              </div>
              <StatusTimeline activeIdx={activeIdx} />
              {status.latest_note && (
                <div className="mt-6 p-4 rounded-md bg-[#f8f4e8] border-l-4 border-[#d6aa43] text-sm text-[#0a0d12] italic">
                  Latest note: {status.latest_note}
                </div>
              )}
              <div className="mt-6 flex gap-3 flex-wrap">
                <button
                  onClick={() => generateApplicationReceipt({
                    serial: status.serial, applicantName: status.applicant_name,
                    category: status.primary_category, categoryLabel: NICE(status.primary_category),
                    dateISO: status.created_at,
                    trackUrl: `${window.location.origin}/apply/track?serial=${encodeURIComponent(status.serial)}`,
                  }).catch(() => toast.error("Could not generate PDF"))}
                  className="dk-btn-outline !text-[#0d1724] !border-[#0d1724] hover:!bg-[#155e3f] hover:!text-[#d6aa43]"
                  data-testid="track-download-receipt"
                >
                  <Download size={14} /> Download receipt (PDF)
                </button>
                {!token && contact && (
                  <button onClick={doUnlock} className="dk-btn-primary"><Unlock size={14} /> Unlock full portal</button>
                )}
              </div>
            </div>
          )}

          {/* Portal (unlocked) */}
          {portalApp && (
            <div className="mt-8 space-y-6" data-testid="portal-view">
              <div className="bg-[#155e3f] text-white rounded-2xl p-8 md:p-10">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-[#d6aa43] font-semibold">Full portal · 24h session</div>
                    <div className="font-mono text-lg font-bold mt-1">{portalApp.serial}</div>
                    <div className="text-sm text-white mt-1">{NICE(portalApp.primary_category)}</div>
                    <h2 className="font-serif text-2xl md:text-3xl font-bold mt-3">Hi {portalApp.applicant_name}.</h2>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white">Current status</div>
                    <div className="font-serif text-2xl font-bold text-[#d6aa43]">{portalApp.status_label}</div>
                    <button onClick={logoutPortal} className="text-xs text-white mt-2 hover:underline" data-testid="portal-logout">Close session</button>
                  </div>
                </div>
                <StatusTimeline activeIdx={STATUS_ORDER.indexOf(portalApp.status)} dark />
              </div>

              {/* Notes + history */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-6 border border-black/5">
                  <h3 className="font-serif text-xl font-bold text-[#0a0d12] mb-4">Status history</h3>
                  <ol className="relative border-l-2 border-[#d6aa43]/30 ml-2 space-y-4" data-testid="portal-history">
                    {portalApp.history.slice().reverse().map((h, i) => (
                      <li key={`${h.at}-${h.status}-${h.note || ''}`} className="ml-4">
                        <div className="absolute -left-[9px] w-4 h-4 rounded-full bg-[#d6aa43]" style={{ transform: `translateY(${i * 0}px)` }} />
                        <div className="text-xs text-[#0a0d12]/60">{new Date(h.at).toLocaleString()}</div>
                        <div className="font-semibold text-[#0a0d12]">{h.status_label}</div>
                        {h.note && <div className="text-sm text-[#0a0d12]/70 italic mt-1">{h.note}</div>}
                      </li>
                    ))}
                    {portalApp.history.length === 0 && <p className="text-sm text-[#0a0d12]/60 ml-2">No status changes yet.</p>}
                  </ol>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-black/5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-serif text-xl font-bold text-[#0a0d12]">Documents</h3>
                    <button onClick={() => fileInputRef.current?.click()} className="dk-btn-primary !py-2 !px-4 text-sm" data-testid="portal-upload-btn">
                      <Upload size={14} /> Upload
                    </button>
                    <input ref={fileInputRef} type="file" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} />
                  </div>
                  <p className="text-xs text-[#0a0d12]/60 mb-3">Up to 10 MB per file. Files stored securely against your application record.</p>
                  <div className="space-y-2" data-testid="portal-docs-list">
                    {docs.map((d) => (
                      <div key={d.id} className="flex items-center gap-3 p-3 rounded-md border border-black/5 hover:bg-[#f8f4e8]">
                        <File size={16} className="text-[#d6aa43]" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate">{d.filename}</div>
                          <div className="text-[10px] text-[#0a0d12]/60">{Math.max(1, Math.round((d.size_bytes || 0) / 1024))} KB · {new Date(d.uploaded_at).toLocaleDateString()}</div>
                        </div>
                        <button onClick={() => downloadDoc(d)} className="p-1.5 hover:bg-white rounded" title="Download"><Download size={14} /></button>
                        <button onClick={() => deleteDoc(d.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    ))}
                    {docs.length === 0 && <p className="text-sm text-[#0a0d12]/60 text-center py-4">No documents uploaded yet.</p>}
                  </div>
                </div>
              </div>

              {portalApp.admin_notes_public && (
                <div className="p-6 rounded-2xl bg-[#f8f4e8] border-l-4 border-[#d6aa43]">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#0d1724]/60 font-semibold mb-2">Foundation notes</div>
                  <p className="text-[#0a0d12] whitespace-pre-wrap">{portalApp.admin_notes_public}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function StatusTimeline({ activeIdx, dark = false }) {
  const past = "bg-[#d6aa43]";
  const cur = "bg-[#d6aa43] ring-4 ring-[#d6aa43]/25";
  const idle = dark ? "bg-white/15" : "bg-black/10";
  return (
    <div className="mt-2">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="track-timeline">
        {STATUS_ORDER.slice(0, 6).map((k, i) => (
          <div key={k} className="p-3 rounded-md border" style={{ borderColor: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}>
            <div className={`w-3 h-3 rounded-full mb-2 ${i < activeIdx ? past : i === activeIdx ? cur : idle}`} />
            <div className={`text-xs font-semibold ${dark ? "text-white" : "text-[#0a0d12]"}`}>{STATUS_LABEL[k]}</div>
            <div className={`text-[10px] mt-1 leading-tight ${dark ? "text-white" : "text-[#0a0d12]/60"}`}>{STATUS_DESC[k]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
